import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "pool-service-web-design-colorado",
  metaTitle: "Pool Service Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom pool service web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Pool Service Web Design Colorado",
  eyebrow: "Pool Service Web Design — Colorado",
  h1: (
    <>
      Pool Service <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded pool service websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Pool Service websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first pool service sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Pool Service web design in Colorado",
    body: "Pool Service web design is the practice of building conversion-focused, mobile-first websites for pool service businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with recurring plan signup above the fold." },
    { title: "Service pages per type", body: "Weekly service, opening, closing, repair, equipment, leak detection — each ranks." },
    { title: "Recurring plan signup", body: "Weekly/biweekly autopay signup above the fold drives LTV." },
    { title: "Online quote + booking", body: "Real-time quote and booking with Skimmer, Pool Brain, or your CRM." },
    { title: "Trust + licensing badges", body: "CPO certification, insurance, and reviews surfaced above the fold." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Recurring is the entire model", body: "Pool service is built on autopay. Recurring plan signup above the fold drives 5x LTV." },
    { heading: "Opening/closing capture seasonal demand", body: "Spring opening and fall closing are seasonal spikes — dedicated pages rank the season." },
    { heading: "Repair and equipment outrank generic pool pages", body: "Heater, pump, and liner repair are urgent searches with different conversion flows." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a pool service website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a pool service website?", a: "Typical timeline is 4–6 weeks for a 12–20 page pool service site with full service and service-area pages. Larger multi-location pool service businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs pool service businesses?", a: "No — we build pool service sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Pool Service Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for pool service businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/pool-service-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
